import { useCallback, useReducer } from 'react';

// Fetch actions:
type StartRequest = { type: 'request', currentAction: string | undefined };
type ReceivedError<T> = { type: 'error'; error: T; currentAction: string | undefined; };
type ReceivedResponse<T> = { type: 'response'; response: T; currentAction: string | undefined; };
type Reset = { type: 'reset' };

type FetchAction<T, TError> = StartRequest | ReceivedError<TError> | ReceivedResponse<T> | Reset;

// Fetch action constructors:
const StartRequest = (currentAction?: string): StartRequest => ({ type: 'request', currentAction });
const ReceivedError = <T>(error: T, currentAction: string | undefined): ReceivedError<T> => ({
  type: 'error',
  currentAction,
  error,
});
const ReceivedResponse = <T>(response: T, currentAction: string | undefined): ReceivedResponse<T> => ({
  type: 'response',
  currentAction,
  response,
});
const Reset: Reset = { type: 'reset' };

type InitialState = {
  type: 'initial';
  currentAction?: string;
};

type PendingState<TResp, TErr> = { type: 'loading'; response?: TResp; requestError?: TErr, currentAction?: string };
type SuccessState<TResp> = { type: 'success'; response: TResp, currentAction?: string };
type ErrorState<TErr> = { type: 'error'; requestError: TErr, currentAction?: string };
export type FetchState<T = any, TError = string> =
  | PendingState<T, TError>
  | InitialState
  | SuccessState<T>
  | ErrorState<TError>;

function fetchReducer<T, TError>(
  oldState: FetchState<T, TError>,
  action: FetchAction<T, TError>,
): FetchState<T, TError> {
  switch (action.type) {
    case 'request':
      return { ...oldState, type: 'loading', currentAction: action.currentAction };
    case 'error':
      return { type: 'error', requestError: action.error, currentAction: action.currentAction };
    case 'response':
      return { type: 'success', response: action.response, currentAction: action.currentAction };
    case 'reset':
      return { type: 'initial' };
    default:
      return oldState;
  }
}

export const useFetchReducer = <T = any, TError = string>() => {
  const [state, dispatch] = useReducer(fetchReducer<T, TError>, { type: 'initial' });

  const startRequest = useCallback((currentAction?: string) => dispatch(StartRequest(currentAction)), [dispatch]);
  const receivedError = useCallback((error: TError, currentAction?: string) =>
    dispatch(ReceivedError<TError>(error, currentAction)), [dispatch]);
  const receivedResponse = useCallback((response: T, currentAction?: string) =>
    dispatch(ReceivedResponse(response, currentAction)), [dispatch]);
  const reset = useCallback(() => dispatch(Reset), [dispatch]);

  return {
    state,
    response: state.type === 'loading' || state.type === 'success' ? state.response : undefined,
    requestError: state.type === 'loading' || state.type === 'error' ? state.requestError : undefined,
    loading: state.type === 'loading',
    startRequest,
    receivedError,
    receivedResponse,
    reset,
  };
};
